import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Container,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
  IconButton,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  Schedule,
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  Send,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { api } from '../../api/api';
import { CONTACT_PHONES, toTelHref } from '../../constants/contactPhones';

const emptyForm = { name: '', phone: '', email: '', message: '' };

const ContactUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const contact = useSelector((state) => state.themeValue?.contact || {});
  const social = useSelector((state) => state.themeValue?.social || {});
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const contactMethods = useMemo(() => {
    const items = [];
    if (contact.address) {
      items.push({
        icon: <LocationOn color="primary" fontSize="large" />,
        title: 'Our Office',
        details: contact.address,
      });
    }
    items.push({
      icon: <Phone color="primary" fontSize="large" />,
      title: 'Phone',
      phones: CONTACT_PHONES,
    });
    if (contact.email) {
      items.push({
        icon: <Email color="primary" fontSize="large" />,
        title: 'Email',
        details: contact.email,
        href: `mailto:${contact.email}`,
      });
    }
    if (contact.workingHours) {
      items.push({
        icon: <Schedule color="primary" fontSize="large" />,
        title: 'Hours',
        details: contact.workingHours,
      });
    }
    return items;
  }, [contact]);

  const socialLinks = useMemo(
    () =>
      [
        { key: 'facebook', icon: <Facebook />, color: '#1877F2', url: social.facebook },
        { key: 'instagram', icon: <Instagram />, color: '#E4405F', url: social.instagram },
        { key: 'twitter', icon: <Twitter />, color: '#1DA1F2', url: social.twitter },
        { key: 'linkedin', icon: <LinkedIn />, color: '#0A66C2', url: social.linkedin },
      ].filter((item) => typeof item.url === 'string' && item.url.trim()),
    [social]
  );

  const mapQuery =
    (typeof contact.address === 'string' && contact.address.trim()) ||
    'Ponmar Jct, Ponmar, Chennai, Tamil Nadu 600127';

  const mapEmbedUrl = `https://maps.google.com/maps?q=${encodeURIComponent(
    mapQuery
  )}&z=15&output=embed`;

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      await api.submitContactEnquiry({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      toast.success('Enquiry submitted successfully. We will get back to you soon.');
      setForm(emptyForm);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        py: 8,
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            textAlign: 'center',
            mb: 4,
            color: theme.palette.text.primary,
          }}
        >
          Contact Us
        </Typography>

        <Typography
          variant="h6"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: 6,
            color: theme.palette.text.secondary,
            maxWidth: '700px',
            mx: 'auto',
          }}
        >
          Have questions about properties, listings, or our services? Reach out to our team - we&apos;re here to help you with all your real estate needs.
        </Typography>

        <Paper
          elevation={3}
          sx={{
            p: isMobile ? 2 : 4,
            borderRadius: 2,
            mb: 6,
          }}
        >
          <Grid container spacing={4}>
            <Grid xs={12} md={6}>
              <Typography
                variant="h5"
                component="h3"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: theme.palette.primary.main,
                }}
              >
                Contact Information
              </Typography>

              <Stack spacing={3}>
                {contactMethods.map((method, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ mt: 0.5 }}>{method.icon}</Box>
                    <Box>
                      <Typography variant="h6" component="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {method.title}
                      </Typography>
                      {method.phones ? (
                        <Stack spacing={0.5}>
                          {method.phones.map((phone) => (
                            <Typography
                              key={phone}
                              variant="body1"
                              component="a"
                              href={toTelHref(phone)}
                              sx={{ color: 'inherit', textDecoration: 'none', display: 'block' }}
                            >
                              {phone}
                            </Typography>
                          ))}
                        </Stack>
                      ) : method.href ? (
                        <Typography
                          variant="body1"
                          component="a"
                          href={method.href}
                          sx={{ whiteSpace: 'pre-line', color: 'inherit', textDecoration: 'none' }}
                        >
                          {method.details}
                        </Typography>
                      ) : (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                          {method.details}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>

              {socialLinks.length > 0 && (
                <>
                  <Divider sx={{ my: 4 }} />
                  <Typography variant="h6" component="h4" sx={{ fontWeight: 600, mb: 2 }}>
                    Follow Us
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    {socialLinks.map((item) => (
                      <IconButton
                        key={item.key}
                        onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
                        aria-label={item.key}
                        sx={{
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.grey[800]
                              : theme.palette.grey[100],
                          color: item.color,
                          '&:hover': {
                            backgroundColor: item.color,
                            color: '#fff',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s ease',
                          width: 40,
                          height: 40,
                        }}
                      >
                        {item.icon}
                      </IconButton>
                    ))}
                  </Stack>
                </>
              )}
            </Grid>

            <Grid xs={12} md={6}>
              <Typography
                variant="h5"
                component="h3"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: theme.palette.primary.main,
                }}
              >
                Find Us
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  height: { xs: 280, sm: 360, md: 400 },
                  borderRadius: '5px',
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.100',
                }}
              >
                <Box
                  component="iframe"
                  title="Office location map"
                  src={mapEmbedUrl}
                  width="100%"
                  height="100%"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  sx={{ border: 0, display: 'block' }}
                />
              </Box>
              {mapQuery && (
                <Typography
                  component="a"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{
                    display: 'inline-block',
                    mt: 1.5,
                    color: 'primary.main',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Open in Google Maps
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={3}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: isMobile ? 2 : 4,
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              variant="h5"
              component="h3"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: 1,
                color: theme.palette.primary.main,
                textAlign: 'center',
              }}
            >
              Send an Enquiry
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, textAlign: 'center' }}
            >
              Fill in your details and we will respond as soon as possible.
            </Typography>

            <Stack spacing={2} sx={{ width: '100%', maxWidth: 480 }}>
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name}
                onChange={handleChange('name')}
                disabled={submitting}
              />
              <TextField
                fullWidth
                required
                label="Phone"
                value={form.phone}
                onChange={handleChange('phone')}
                disabled={submitting}
              />
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                value={form.email}
                onChange={handleChange('email')}
                disabled={submitting}
              />
              <TextField
                fullWidth
                required
                multiline
                minRows={4}
                label="Message"
                value={form.message}
                onChange={handleChange('message')}
                disabled={submitting}
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Send />}
                  sx={{ px: 4, py: 1.25, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  {submitting ? 'Submitting...' : 'Submit Enquiry'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Container>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Box>
  );
};

export default ContactUs;
